// Contenus de la refonte 2026 (home + pages produit), FR / EN / ES
const P = "/assets/product";

const fr = {
  humanQuote: {
    quote: "On a enfin eu des mots pour dire ce qu'un bon manager fait vraiment. Plus une impression : des faits.",
    attribution: "Une DRH, après huit semaines de pilote",
    lead: "Cayus ne remplace pas votre jugement. Il vous donne les mots et les exemples pour en parler.",
  },

  nav: {
    product: "Produit",
    managers: "Pour les managers",
    senior: "Pour les senior managers",
    hr: "Pour les RH",
    exco: "Pour le comité exécutif",
    methodology: "Méthodologie",
    pilotProgram: "Programme pilote",
    security: "Sécurité & conformité",
    why: "Pourquoi Cayus",
    blog: "Blog",
    resources: "Ressources",
    company: "Entreprise",
    tagline: "Des comportements mesurés, jamais des personnes.",
    legal: "Fondée en 2024 · Londres (HQ) & Paris",
  },

  hero: {
    eyebrow: "Le management, mesuré. Pas deviné.",
    subheadline:
      "Avec l'accord de chaque manager, Cayus observe sa façon de travailler dans Microsoft Teams. Chaque semaine, il lui montre ce qui fonctionne et ce qu'il peut ajuster. On mesure des habitudes de travail, jamais des personnes.",
    badges: ["Application Microsoft Teams", "Des mots, pas des notes", "Conforme RGPD"],
    secondaryCta: "Comprendre comment ça marche →",
    caption: "L'onglet Cayus du manager",
  },
  cost: {
    eyebrow: "L'angle mort",
    title: "Ce que l'angle mort vous coûte",
    lead: "C'est le management qui fait la performance. Et c'est la seule chose que personne ne mesure. On s'en aperçoit quand la facture arrive.",
    facts: [
      "Personne ne voit comment vos managers managent au quotidien. On réagit donc toujours trop tard.",
      "L'enquête annuelle donne un ressenti, une fois par an, quand tout est déjà joué.",
      "Le désengagement s'installe en silence. Il se voit dans les comportements bien avant les résultats.",
    ],
    timelineLabel: "Le décalage de l'angle mort",
    timeline: ["Le problème commence", "Invisible pendant des mois", "Émerge quand le changement cale", "Trop tard, la facture arrive"],
    quote: "Vous n'attendriez pas un diagnostic pour surveiller une santé qui décline. Le leadership ne devrait pas être différent.",
  },
  altitudes: {
    eyebrow: "Quatre vues",
    title: "Chacun voit ce dont il a besoin, et rien de plus",
    lead: "Une même mesure, quatre vues. Chacun voit seulement ce sur quoi il peut agir.",
    guaranteeLabel: "Garantie de confidentialité",
    linkPrefix: "Découvrir la vue",
    items: [
      {
        tab: "Manager",
        href: "/produit/managers",
        img: `${P}/01-manager-two-clocks.webp`,
        caption: "La vue du manager",
        text: "Un comportement à travailler cette semaine, et ses progrès sur la durée juste à côté. Tout se passe dans son onglet Teams.",
        guarantee: "Ses propres mots, visibles de lui seul. Jamais enregistrés, jamais partagés.",
      },
      {
        tab: "Senior manager",
        href: "/produit/senior-managers",
        img: `${P}/06-senior-two-clocks.webp`,
        caption: "La vue du senior manager",
        text: "Ses propres comportements, et l'état de ses relations avec chaque manager : qui a besoin de lui cette semaine, et quelle relation s'est refroidie.",
        guarantee: "Son agenda à lui, rien d'autre. Jamais la vue privée de ses managers.",
      },
      {
        tab: "RH",
        href: "/produit/rh",
        img: `${P}/10-hr-three-clocks.webp`,
        caption: "La vue RH",
        text: "Chaque comportement, chaque service, mois après mois. Vous savez où aider, et vous pouvez le montrer au comité.",
        guarantee: "Des groupes de 8 personnes minimum, décrits avec des mots. Jamais une personne.",
      },
      {
        tab: "Comité exécutif",
        href: "/produit/comite-executif",
        img: `${P}/14-exco-two-clocks.webp`,
        caption: "La vue du comité exécutif",
        text: "Ce que l'entreprise tient vraiment dans le temps, et ce que deviennent les décisions du comité, son propre rythme compris.",
        guarantee: "Des tendances de groupe, celle du comité comprise.",
      },
    ],
  },
  ladders: {
    eyebrow: "Ce que Cayus lit",
    title: "Six pratiques qui font la différence",
    lead: "Six comportements observés dans le travail de tous les jours, expliquées en une phrase. Chacun est comparé à lui-même, jamais aux autres.",
    items: [
      { n: "Clarté des demandes", en: "Clear asks", d: "Des demandes qui portent le quoi, le qui et le quand.", ex: "Vos demandes portent le quoi et le qui ; le quand glisse encore." },
      { n: "Investissement relationnel", en: "Relationship investment", d: "L'attention réellement donnée à chaque relation de travail.", ex: "Les réponses à votre équipe attendent plus longtemps que celles vers le haut." },
      { n: "Reconnaissance", en: "Recognition", d: "Reconnaître les efforts autant que les résultats.", ex: "Vous avez reconnu les résultats plus que les efforts cette fenêtre." },
      { n: "Rythme soutenable", en: "Sustainable rhythm", d: "Un rythme qui tient dans la durée, pour vous et pour l'équipe.", ex: "Votre envoi en soirée a augmenté ces quinze derniers jours." },
      { n: "Développement", en: "Development focus", d: "Le temps consacré à apprendre et à faire grandir.", ex: "Votre temps d'apprentissage a disparu en semaine cinq et n'est pas revenu." },
      { n: "Connecter l'organisation", en: "Connecting the organisation", d: "Créer des liens qui vivent sans vous. (Senior)", ex: "Le binôme que vous avez créé a continué trois fois sans vous." },
    ],
    exampleLabel: "Exemple de lecture",
    levels: "Quatre étapes avec des noms, jamais des notes : Emerging, Establishing, Consistent, Multiplying. Et « Building Evidence » quand il n'y a pas encore assez d'exemples pour se prononcer.",
    note: "Pendant les périodes de changement, deux comportements s'ajoutent : expliquer le changement, et avancer malgré l'incertitude.",
    link: "Voir la méthodologie complète",
  },
  how: {
    eyebrow: "La mise en place",
    title: "Trois étapes, aucune formation à programmer",
    steps: [
      { title: "Chacun donne son accord", text: "Chaque manager relie son propre compte, et peut le débrancher quand il veut. Sans son accord, rien n'est lu." },
      { title: "Les preuves s'accumulent", text: "Cayus regarde les 8 dernières semaines et les compare aux précédentes. Chaque texte est lu une fois, puis effacé." },
      { title: "Chacun ouvre son onglet", text: "Dans Teams, chacun ouvre sa propre vue. Aucun nouveau mot de passe, aucun outil à apprendre." },
    ],
    promise: "Aucun score, pourcentage ou classement ne sort jamais de Cayus.",
    europe: "Pensé pour l'Europe : RGPD, dialogue avec le CSE, conforme au règlement européen sur l'IA",
    link: "Voir la sécurité & la conformité",
  },
  trust: {
    eyebrow: "Confiance",
    title: "Pourquoi les RH nous font confiance",
    lead: "Ce que voit un manager n'est visible que de lui. Les RH ne voient que des groupes, décrits avec des mots.",
  },
  liveCard: {
    label: "Cette semaine",
    practice: "Recognition",
    from: "Establishing",
    to: "Consistent",
    evidence: "Le comportement tient depuis quatre semaines",
  },
  wallViz: {
    label: "Le mur, en une image",
    inLabel: "Ce qui entre",
    inItems: ["« Tu peux regarder ça ? »", "Un point de 30 minutes", "« Merci, beau travail »"],
    wall: "LE MUR",
    outLabel: "Ce qui en sort",
    outItems: ["Clear asks · Consistent", "Recognition · Establishing"],
    note: "Chaque message est lu une fois, puis effacé. Il n'en sort que des mots : jamais un score, jamais un nom, jamais le texte.",
  },
  pilotTimeline: {
    label: "Huit semaines, trois rendez-vous",
    steps: [
      { w: "Semaine 1", t: "Installation", d: "Une heure, dans votre Teams." },
      { w: "Semaines 2-3", t: "Les premières preuves", d: "Chacun ouvre son propre onglet." },
      { w: "Semaine 4", t: "Les premiers comportements", d: "Une seule chose par manager." },
      { w: "Semaine 8", t: "Restitution au comité", d: "Ce qui a bougé, avec des preuves." },
    ],
  },
  beforeAfter: {
    eyebrow: "Avant / après",
    title: "Le même trimestre, vu de deux façons.",
    lead: "Faites glisser la poignée : à gauche ce que vous voyez aujourd'hui, à droite ce que vous verriez avec Cayus.",
    beforeLabel: "Sans Cayus",
    afterLabel: "Avec Cayus",
    before: [
      "Vous apprenez qu'un manager décroche le jour de sa démission",
      "L'enquête recueille ce qu'on veut bien déclarer",
      "« Il faut mieux communiquer » : personne ne sait quoi faire lundi",
      "Le budget formation se défend avec un taux de participation",
    ],
    after: [
      "La relation qui se fragilise est visible quand un café suffit encore",
      "Vous voyez ce qui se passe ce mois-ci, service par service",
      "Chaque manager a une seule chose à travailler cette semaine",
      "Vous montrez au comité un changement réellement observé",
    ],
    hint: "Glissez pour comparer",
  },
  brief: {
    navLabel: "Dossier exécutif",
    eyebrow: "Dossier exécutif",
    title: "Deux pages à poser sur la table de votre comité.",
    lead: "Tout ce qu'il faut pour présenter Cayus en interne : ce que l'on mesure, ce qui ne sort jamais, et comment se passe un pilote. Rien à lire en diagonale.",
    bullets: [
      "Le point aveugle : ce que vos KPIs ne montrent pas",
      "Les six pratiques suivies, et les quatre vues (manager, senior, RH, comité)",
      "Le mur : ce qui traverse, ce qui ne traverse jamais",
      "Confidentialité, RGPD, règlement européen sur l'IA, et le déroulé du pilote",
    ],
    format: "PDF · 2 pages · prêt à imprimer",
    form: {
      name: "Nom et prénom",
      namePlaceholder: "Camille Martin",
      email: "Email professionnel",
      emailPlaceholder: "prenom.nom@entreprise.com",
      company: "Entreprise",
      role: "Fonction (facultatif)",
      rolePlaceholder: "DRH, directeur général…",
      submit: "Recevoir le dossier",
      submitting: "Envoi…",
      note: "Téléchargement immédiat, et une copie arrive dans votre boîte. Pas de newsletter.",
    },
    success: {
      title: "C'est parti",
      text: "Le téléchargement démarre dans un nouvel onglet. Une copie du lien vient aussi de partir par email.",
      download: "Télécharger le dossier",
      toast: "Dossier envoyé. Le téléchargement démarre.",
    },
    error: "Le téléchargement n'a pas pu démarrer. Merci de réessayer.",
  },
  finalCta: {
    badge: "5 places de pilote",
    title: "Mesurez ce que vos KPIs ne voient pas.",
    button: "Voir ce que Cayus mesurerait chez vous",
  },
  product: {
    ctaSection: "Trente minutes suffisent pour voir ce que ça donnerait chez vous.",
    managers: {
      eyebrow: "Pour les managers",
      title: "Devenir un meilleur manager, une semaine à la fois.",
      intro: "Aucune formation à organiser, aucun outil à apprendre. Cayus vit dans Teams : il propose une seule chose à travailler, puis montre que ce comportement s'installe.",
      promises: ["Une seule chose par semaine, jamais une liste de reproches", "Dans Teams, sans nouveau mot de passe", "Vos mots restent les vôtres"],
      cta: "Voir ce que Cayus dirait à vos managers",
      blocks: [
        { img: `${P}/02-manager-focus.webp`, caption: "Le focus de la semaine", heading: "Un geste concret, pas un rapport de quarante pages", text: "Un seul comportement à travailler, en une phrase. Elle est validée quand elle se répète dans plusieurs situations : c'est une preuve, pas une promesse." },
        { img: `${P}/03-manager-verbatim.webp`, caption: "Vos propres mots, visibles de vous seul", heading: "Vous voyez ce qui a compté, dans vos mots", text: "Derrière chaque observation, les phrases exactes qui l'ont déclenchée. Vous savez quoi écrire autrement dès le prochain message.", box: "Ses propres mots, visibles de lui seul et seulement panneau ouvert. Jamais stockés, jamais notés, jamais partagés." },
        { img: `${P}/04-manager-staircase.webp`, caption: "Quatre niveaux nommés", heading: "Une progression, pas une note", text: "On passe à l'étape suivante quand le comportement tient dans le temps, pas après une bonne semaine. Et personne n'est comparé à personne." },
        { img: `${P}/05-manager-habits.webp`, caption: "Les six pratiques suivies", heading: "Six pratiques qui changent tout pour une équipe", text: "Clarté des demandes, relations, reconnaissance, rythme de travail, temps passé à faire progresser. Quand il n'y a pas assez d'exemples, Cayus le dit au lieu de deviner." },
      ],
    },
    "senior-managers": {
      eyebrow: "Pour les senior managers",
      title: "Vous ne pouvez pas être partout. Cayus vous dit où regarder.",
      intro: "Vos comportements, et l'état de vos relations avec chaque manager, vu depuis votre seul agenda. La relation qui se fragilise devient visible avant de casser.",
      promises: ["La même vue que vos managers, à votre niveau", "Depuis votre agenda, jamais leur vue privée", "Un signal avant la démission"],
      cta: "Voir ce que Cayus révélerait dans votre équipe",
      blocks: [
        { img: `${P}/06-senior-two-clocks.webp`, caption: "Votre pratique, votre trajectoire", heading: "Ce que vous faites cette semaine, et ce que vous construisez", text: "La même lecture que celle de vos managers, à votre niveau. Vous parlez enfin le même langage qu'eux." },
        { img: `${P}/08-senior-span.webp`, caption: "Vos relations directes, une par une", heading: "Vos six, une relation à la fois", text: "Fréquence de vos entretiens, demandes envoyées, temps passé à faire progresser. Trois états lisibles d'un coup d'œil, pour savoir qui a besoin de vous cette semaine." },
        { img: `${P}/09-senior-balance.webp`, caption: "Ce que vous avez donné, ce qui a glissé", heading: "Votre attention est une ressource. Voyez où elle est passée.", text: "Une relation ne se refroidit jamais exprès. Cayus le montre quand un café suffit encore à rattraper, avant qu'il faille recruter." },
        { img: `${P}/07-senior-staircase.webp`, caption: "La progression au niveau senior", heading: "Une échelle de plus : connecter l'organisation", text: "Créer des liens qui continuent à vivre sans vous.", box: "Vous voyez ce que vous avez fait. Jamais ce que Cayus a dit à vos managers, jamais leur étape." },
      ],
    },
    rh: {
      eyebrow: "Pour les RH",
      title: "Enfin une lecture du leadership entre deux enquêtes annuelles.",
      intro: "Chaque comportement, chaque service, mois après mois, décrit avec des mots, jamais avec des noms. Et vos formations deviennent enfin démontrables au comité.",
      promises: ["En continu, pas une photo une fois par an", "Des groupes de 8 personnes minimum, jamais de noms", "Votre budget formation, justifié par un changement observé"],
      cta: "Voir ce que Cayus lirait dans votre organisation",
      blocks: [
        { img: `${P}/10-hr-three-clocks.webp`, caption: "La lecture d'ensemble", heading: "Vous n'attendez plus l'enquête de l'an prochain", text: "L'enquête annuelle raconte ce qui s'est passé. Cayus montre ce qui se passe en ce moment." },
        { img: `${P}/12-hr-units.webp`, caption: "Pratiques × unités", heading: "Où ça monte, où ça glisse", text: "L'étape où se situe chaque service, et son évolution par rapport à ses propres mois. Vous savez où aider, et où il n'y a rien à réparer." },
        { img: `${P}/11-hr-matrix.webp`, caption: "Les playbooks", heading: "Votre budget développement, enfin défendable", text: "Chaque programme annonce le changement attendu et une date. En comité, vous ne présentez plus un taux de participation, mais un changement réellement observé.", box: "Ce que les RH voient : des services, des tendances, des évolutions. Ce qu'elles ne voient jamais : une personne, son étape, le contenu d'un message." },
      ],
    },
    "comite-executif": {
      eyebrow: "Pour le comité exécutif",
      title: "Ce que l'organisation tient vraiment, et ce que deviennent vos décisions.",
      intro: "Une stratégie ne meurt pas en réunion : elle se dilue entre deux comités. Cayus montre ce qui tient vraiment et ce que devient chaque décision, le rythme du comité compris.",
      promises: ["Des tendances de groupe, jamais des personnes", "Chaque décision avec un responsable et une date", "Le rythme du comité inclus, l'exemple compte aussi"],
      cta: "Voir ce que Cayus mesurerait chez vous",
      blocks: [
        { img: `${P}/14-exco-two-clocks.webp`, caption: "Ce qui tient dans le temps", heading: "Ce qui tient dans la durée, pas ce qui a été annoncé", text: "Vous voyez si la culture que vous décrivez est celle qui se vit vraiment." },
        { img: `${P}/15-exco-questions.webp`, caption: "Le devenir des décisions", heading: "Ce qu'est devenue la dernière décision", text: "Tenue, en partie, pas encore, avec un responsable et une date. Plus rien ne se perd discrètement entre deux réunions." },
        { img: `${P}/16-exco-decisions.webp`, caption: "L'ombre du comité", heading: "Votre rythme est celui que l'organisation copie", text: "Le rythme du comité, vu comme une tendance de groupe et jamais attribué à quelqu'un, avec les gestes concrets pour l'ajuster.", quote: "Le rythme que vous tenez est celui que l'organisation copie." },
        { img: `${P}/17-exco-shadow.webp`, caption: "Le registre des décisions", heading: "Le compte rendu s'écrit tout seul", text: "Chaque décision, son responsable et la date où on la revoit, à copier en un clic dans le compte rendu." },
      ],
    },
  },
};

const en = {
  humanQuote: {
    quote: "We finally had language for what good leadership actually looked like. Not a hunch, a reading.",
    attribution: "An HR director, after eight weeks of pilot",
    lead: "Cayus doesn't replace your judgement. It gives you the vocabulary and the evidence to talk about it.",
  },

  nav: {
    product: "Product",
    managers: "For managers",
    senior: "For senior managers",
    hr: "For HR",
    exco: "For the executive committee",
    methodology: "Methodology",
    pilotProgram: "Pilot programme",
    security: "Security & compliance",
    why: "Why Cayus",
    blog: "Blog",
    resources: "Resources",
    company: "Company",
    tagline: "Behaviours measured, never people.",
    legal: "Founded in 2024 · London (HQ) & Paris",
  },

  hero: {
    eyebrow: "Leadership, measured. Not guessed.",
    subheadline:
      "With each manager's consent, Cayus looks at how they work inside Microsoft Teams. Every week it shows them what is working and what to adjust. We measure working habits, never people.",
    badges: ["Microsoft Teams app", "Words, not scores", "GDPR compliant"],
    secondaryCta: "See how it works →",
    caption: "The manager's Cayus tab",
  },
  cost: {
    eyebrow: "The blind spot",
    title: "What the blind spot costs you",
    lead: "Leadership is what drives performance. And it is the one thing nobody measures. You find out when the bill arrives.",
    facts: [
      "Nobody sees how your managers manage day to day. So you always react too late.",
      "The annual survey gives you a feeling, once a year, when it is already too late.",
      "Disengagement sets in quietly. It shows in behaviour long before it shows in the numbers.",
    ],
    timelineLabel: "The blind spot lag",
    timeline: ["The problem starts", "Invisible for months", "Surfaces when change stalls", "Too late, the bill arrives"],
    quote: "You wouldn't wait for a diagnosis to watch declining health. Leadership shouldn't be any different.",
  },
  altitudes: {
    eyebrow: "Four views",
    title: "Everyone sees what they need, and nothing more",
    lead: "One measurement, four views. Each person sees only what they can act on.",
    guaranteeLabel: "Privacy guarantee",
    linkPrefix: "Explore the",
    items: [
      {
        tab: "Manager",
        href: "/produit/managers",
        img: `${P}/01-manager-two-clocks.webp`,
        caption: "The manager's view",
        text: "One practice to work on this week, with the long game beside it. Nothing new to learn: it all lives in their Teams tab.",
        guarantee: "Their own words, visible to them alone. Never stored, never shared.",
      },
      {
        tab: "Senior manager",
        href: "/produit/senior-managers",
        img: `${P}/06-senior-two-clocks.webp`,
        caption: "The senior manager's view",
        text: "Their own practice, plus the health of their direct relationships: who needs them this week, and which relationship has slipped.",
        guarantee: "Their own diary only. Never their team's private view.",
      },
      {
        tab: "HR",
        href: "/produit/rh",
        img: `${P}/10-hr-three-clocks.webp`,
        caption: "The HR view",
        text: "Every practice, every unit, month after month. Where to place enablement, and the evidence to prove it to the board.",
        guarantee: "Groups of eight people minimum, described in words. Never a person.",
      },
      {
        tab: "Executive committee",
        href: "/produit/comite-executif",
        img: `${P}/14-exco-two-clocks.webp`,
        caption: "The executive committee view",
        text: "What the organisation sustains over time, and what becomes of the committee's decisions, including its own pace.",
        guarantee: "Group trends, including the committee's own.",
      },
    ],
  },
  ladders: {
    eyebrow: "What Cayus reads",
    title: "Six practices that make the difference",
    lead: "Six behaviours observed in everyday work, each explained in one sentence. Everyone is compared with themselves, never with others.",
    items: [
      { n: "Clear asks", en: "", d: "Asks that carry the what, the who and the when.", ex: "Your asks carry the what and the who; the when still slips." },
      { n: "Relationship investment", en: "", d: "The attention actually given to each working relationship.", ex: "Replies to your own team wait longer than replies upward." },
      { n: "Recognition", en: "", d: "Recognising effort as much as results.", ex: "You recognised results more than effort this window." },
      { n: "Sustainable rhythm", en: "", d: "A pace that holds over time, for you and for the team.", ex: "Your evening sending has increased over the last fortnight." },
      { n: "Development focus", en: "", d: "The time spent learning and growing others.", ex: "Your learning time disappeared in week five and hasn't returned." },
      { n: "Connecting the organisation", en: "", d: "Building links that live without you. (Senior)", ex: "The pairing you created carried on three times without you." },
    ],
    exampleLabel: "Example reading",
    levels: "Four named steps, never a score: Emerging, Establishing, Consistent, Multiplying. And Building Evidence when there aren't enough examples yet.",
    note: "During periods of change, two more behaviours are added: making sense of the change, and moving forward despite uncertainty.",
    link: "See the full methodology",
  },
  how: {
    eyebrow: "Getting started",
    title: "Three steps, no training to schedule",
    steps: [
      { title: "Everyone opts in", text: "Each manager connects their own account, and can unplug it whenever they want. Without their consent, nothing is read." },
      { title: "Evidence builds up", text: "Cayus looks at the last 8 weeks and compares them with the weeks before. Each text is read once, then deleted." },
      { title: "Everyone opens their tab", text: "In Teams, everyone opens their own view. No new password, no new tool to learn." },
    ],
    promise: "No score, percentage or ranking ever leaves Cayus.",
    europe: "Built for Europe: GDPR, works council consultation, compliant with the EU AI Act",
    link: "See security & compliance",
  },
  trust: {
    eyebrow: "Trust",
    title: "Why HR teams trust us",
    lead: "What a manager sees stays with them. HR only ever sees groups, described in words.",
  },
  liveCard: {
    label: "This week",
    practice: "Recognition",
    from: "Establishing",
    to: "Consistent",
    evidence: "The behaviour has held for four weeks",
  },
  wallViz: {
    label: "The wall, in one picture",
    inLabel: "What goes in",
    inItems: ["\"Can you take a look at this?\"", "A 30-minute check-in", "\"Thanks, great work\""],
    wall: "THE WALL",
    outLabel: "What comes out",
    outItems: ["Clear asks · Consistent", "Recognition · Establishing"],
    note: "Each message is read once, then deleted. Only words come out: never a score, never a name, never the text.",
  },
  pilotTimeline: {
    label: "Eight weeks, three moments",
    steps: [
      { w: "Week 1", t: "Set-up", d: "One hour, inside your own Teams." },
      { w: "Weeks 2-3", t: "First evidence", d: "Everyone opens their own tab." },
      { w: "Week 4", t: "First behaviours", d: "One thing per manager." },
      { w: "Week 8", t: "Committee read-out", d: "What moved, with evidence." },
    ],
  },
  beforeAfter: {
    eyebrow: "Before / after",
    title: "The same quarter, seen two ways.",
    lead: "Drag the handle: on the left what you see today, on the right what you would see with Cayus.",
    beforeLabel: "Without Cayus",
    afterLabel: "With Cayus",
    before: [
      "You learn a manager has checked out on the day they resign",
      "The survey collects what people are willing to declare",
      "\"Communicate better\": nobody knows what to do on Monday",
      "The development budget is defended with attendance figures",
    ],
    after: [
      "A cooling relationship shows up while a coffee still fixes it",
      "You see what is happening this month, unit by unit",
      "Every manager has one thing to work on this week",
      "You show the committee a change that was actually observed",
    ],
    hint: "Drag to compare",
  },
  brief: {
    navLabel: "Executive brief",
    eyebrow: "Executive brief",
    title: "Two pages to put on your committee's table.",
    lead: "Everything you need to present Cayus internally: what is measured, what never leaves, and how a pilot runs. Nothing to skim.",
    bullets: [
      "The blind spot: what your KPIs don't show",
      "The six practices tracked, and the four views (manager, senior, HR, committee)",
      "The wall: what crosses, what never crosses",
      "Privacy, GDPR, the EU AI Act, and how the pilot runs",
    ],
    format: "PDF · 2 pages · print-ready",
    form: {
      name: "Full name",
      namePlaceholder: "Alex Morgan",
      email: "Work email",
      emailPlaceholder: "name@company.com",
      company: "Company",
      role: "Role (optional)",
      rolePlaceholder: "HR director, CEO…",
      submit: "Get the brief",
      submitting: "Sending…",
      note: "Instant download, plus a copy in your inbox. No newsletter.",
    },
    success: {
      title: "Here you go",
      text: "The download is opening in a new tab. A copy of the link has just been emailed to you.",
      download: "Download the brief",
      toast: "Brief sent. Your download is starting.",
    },
    error: "The download could not start. Please try again.",
  },
  finalCta: {
    badge: "5 pilot places",
    title: "Measure what your KPIs can't see.",
    button: "See what Cayus would measure",
  },
  product: {
    ctaSection: "Thirty minutes is enough to see what this would look like in your organisation.",
    managers: {
      eyebrow: "For managers",
      title: "Becoming a better manager, one week at a time.",
      intro: "No training to schedule, no tool to learn. Cayus lives in Teams and offers one concrete move to work on, then shows it has taken hold.",
      promises: ["One focus a week, never a list of complaints", "Inside Teams, no new password", "Your words stay yours"],
      cta: "See what Cayus would tell your managers",
      blocks: [
        { img: `${P}/02-manager-focus.webp`, caption: "This week's focus", heading: "One concrete move, not a forty-page report", text: "A single practice to work on, in one sentence. It is validated when it holds across several contexts: evidence, not a promise." },
        { img: `${P}/03-manager-verbatim.webp`, caption: "Your own words, visible to you alone", heading: "You see what counted, in your own words", text: "Behind every reading, the exact sentences that produced it. You know what to phrase differently in the very next message.", box: "Their own words, visible to them alone and only while the panel is open. Never stored, never scored, never shared." },
        { img: `${P}/04-manager-staircase.webp`, caption: "Four named levels", heading: "A progression, not a grade", text: "You move up when the practice holds over time, not because you had a good week. And nobody is compared with anybody." },
        { img: `${P}/05-manager-habits.webp`, caption: "The six practices tracked", heading: "Six practices that change everything for a team", text: "Clear asks, relationships, recognition, rhythm, development. When the evidence is missing, Cayus says so rather than guessing." },
      ],
    },
    "senior-managers": {
      eyebrow: "For senior managers",
      title: "You can't be everywhere. Cayus tells you where to look.",
      intro: "Your own practice, and the health of your direct relationships, from your own diary. The relationship going fragile becomes visible before it breaks.",
      promises: ["The same reading as your managers, at your level", "From your own diary, never their private view", "A signal before the resignation"],
      cta: "See what Cayus would reveal in your team",
      blocks: [
        { img: `${P}/06-senior-two-clocks.webp`, caption: "Your practice, your trajectory", heading: "What you're doing this week, and what you're building", text: "The same reading as your managers, at your level. You finally speak the same language." },
        { img: `${P}/08-senior-span.webp`, caption: "Your direct relationships, one by one", heading: "Your six, one relationship at a time", text: "One-to-one rhythm, asks sent, development given. Three states at a glance, so you know who needs you this week." },
        { img: `${P}/09-senior-balance.webp`, caption: "What you gave, what slipped", heading: "Your attention is a resource. See where it went.", text: "A relationship never slips on purpose. Cayus shows it while catching up still costs a coffee, not a hire." },
        { img: `${P}/07-senior-staircase.webp`, caption: "The progression at senior level", heading: "One more ladder: connecting the organisation", text: "Building links that carry on living without you.", box: "You see what you did. Never what Cayus told your managers, never their level." },
      ],
    },
    rh: {
      eyebrow: "For HR",
      title: "At last, a reading of leadership between two annual surveys.",
      intro: "Every practice, every unit, month after month, described in words, never in people. And your development programmes become provable to the board.",
      promises: ["Continuous, not a once-a-year snapshot", "Groups of eight people minimum, never names", "Your development budget, proven by observed movement"],
      cta: "See what Cayus would read in your organisation",
      blocks: [
        { img: `${P}/10-hr-three-clocks.webp`, caption: "The whole picture", heading: "You no longer wait for next year's survey", text: "Where the annual survey tells you what happened, Cayus shows what is happening." },
        { img: `${P}/12-hr-units.webp`, caption: "Practices × units", heading: "Where it climbs, where it slips", text: "Each unit's dominant level and its movement against its own months. You know where to place enablement, and where nothing needs fixing." },
        { img: `${P}/11-hr-matrix.webp`, caption: "The playbooks", heading: "Your development budget, finally defensible", text: "Every programme carries an expected movement and a date. At the board you no longer present attendance: you present observed change.", box: "What HR sees: teams, trends, changes. What HR never sees: a person, their step, the content of a message." },
      ],
    },
    "comite-executif": {
      eyebrow: "For the executive committee",
      title: "What the organisation really sustains, and what becomes of your decisions.",
      intro: "Strategy doesn't die in the room, it dissolves between two committees. Cayus shows what really holds over time, and what becomes of every decision, including the committee's own pace.",
      promises: ["Group trends, never people", "Every decision with an owner and a date", "The committee's own pace included"],
      cta: "See what Cayus would measure",
      blocks: [
        { img: `${P}/14-exco-two-clocks.webp`, caption: "What holds over time", heading: "What holds over time, not what was announced", text: "You see whether the culture you describe is the one being practised." },
        { img: `${P}/15-exco-questions.webp`, caption: "What became of the decisions", heading: "What became of the last decision", text: "Held, partly, not yet, with an owner and a date. Nothing quietly disappears between two meetings." },
        { img: `${P}/16-exco-decisions.webp`, caption: "The committee's own shadow", heading: "Your pace is the one the organisation copies", text: "The committee's rhythm, read as a group trend and never attributed to one member, with the concrete moves to adjust it.", quote: "The pace you set is the pace the organisation copies." },
        { img: `${P}/17-exco-shadow.webp`, caption: "The decision record", heading: "The minutes write themselves", text: "Every decision, its owner and its check-back date, copied into the minutes in one click." },
      ],
    },
  },
};

const es = {
  humanQuote: {
    quote: "Por fin tuvimos palabras para describir lo que hace un buen manager. No una intuición, una lectura.",
    attribution: "Una directora de RRHH, tras ocho semanas de piloto",
    lead: "Cayus no sustituye su juicio. Le da el vocabulario y la evidencia para hablar de ello.",
  },

  nav: {
    product: "Producto",
    managers: "Para managers",
    senior: "Para senior managers",
    hr: "Para RRHH",
    exco: "Para el comité ejecutivo",
    methodology: "Metodología",
    pilotProgram: "Programa piloto",
    security: "Seguridad y conformidad",
    why: "Por qué Cayus",
    blog: "Blog",
    resources: "Recursos",
    company: "Empresa",
    tagline: "Comportamientos medidos, nunca personas.",
    legal: "Fundada en 2024 · Londres (HQ) y París",
  },

  hero: {
    eyebrow: "El management, medido. No adivinado.",
    subheadline:
      "Con el permiso de cada manager, Cayus observa cómo trabaja dentro de Microsoft Teams. Cada semana le muestra qué funciona y qué puede ajustar. Medimos hábitos de trabajo, nunca personas.",
    badges: ["Aplicación Microsoft Teams", "Palabras, no notas", "Conforme al RGPD"],
    secondaryCta: "Ver cómo funciona →",
    caption: "La pestaña Cayus del manager",
  },
  cost: {
    eyebrow: "El punto ciego",
    title: "Lo que le cuesta el punto ciego",
    lead: "El management es lo que produce el rendimiento. Y es lo único que nadie mide. Uno se da cuenta cuando llega la factura.",
    facts: [
      "Nadie ve cómo gestionan sus managers en el día a día. Por eso siempre se reacciona tarde.",
      "La encuesta anual da una sensación, una vez al año, cuando ya está todo decidido.",
      "La desconexión se instala en silencio. Se ve en los comportamientos mucho antes que en los resultados.",
    ],
    timelineLabel: "El desfase del punto ciego",
    timeline: ["El problema empieza", "Invisible durante meses", "Aparece cuando el cambio se atasca", "Demasiado tarde, llega la factura"],
    quote: "No esperaría un diagnóstico para vigilar una salud que se deteriora. El liderazgo no debería ser distinto.",
  },
  altitudes: {
    eyebrow: "Cuatro vistas",
    title: "Cada uno ve lo que necesita, y nada más",
    lead: "Una misma medición, cuatro vistas. Cada uno ve solo aquello sobre lo que puede actuar.",
    guaranteeLabel: "Garantía de confidencialidad",
    linkPrefix: "Descubrir la vista",
    items: [
      {
        tab: "Manager",
        href: "/produit/managers",
        img: `${P}/01-manager-two-clocks.webp`,
        caption: "La vista del manager",
        text: "Una práctica que trabajar esta semana, y la trayectoria larga al lado. Nada nuevo que aprender: todo vive en su pestaña de Teams.",
        guarantee: "Sus propias palabras, visibles solo para él. Nunca almacenadas, nunca compartidas.",
      },
      {
        tab: "Senior manager",
        href: "/produit/senior-managers",
        img: `${P}/06-senior-two-clocks.webp`,
        caption: "La vista del senior manager",
        text: "Su propia práctica, más la salud de sus relaciones directas: quién le necesita esta semana y qué relación se ha enfriado.",
        guarantee: "Solo su propia agenda, nunca la vista privada de sus equipos.",
      },
      {
        tab: "RRHH",
        href: "/produit/rh",
        img: `${P}/10-hr-three-clocks.webp`,
        caption: "La vista de RRHH",
        text: "Cada práctica, cada unidad, mes a mes. Dónde colocar un acompañamiento y con qué demostrarlo al comité.",
        guarantee: "Grupos de 8 personas como mínimo, descritos con palabras. Nunca una persona.",
      },
      {
        tab: "Comité ejecutivo",
        href: "/produit/comite-executif",
        img: `${P}/14-exco-two-clocks.webp`,
        caption: "La vista del comité ejecutivo",
        text: "Lo que la organización sostiene en el tiempo y en qué acaban las decisiones del comité, incluido su propio ritmo.",
        guarantee: "Tendencias de grupo, incluida la del comité.",
      },
    ],
  },
  ladders: {
    eyebrow: "Lo que Cayus lee",
    title: "Seis prácticas que marcan la diferencia",
    lead: "Seis comportamientos observados en el trabajo diario, explicados en una frase. Cada uno se compara consigo mismo, nunca con los demás.",
    items: [
      { n: "Peticiones claras", en: "Clear asks", d: "Peticiones que llevan el qué, el quién y el cuándo.", ex: "Sus peticiones llevan el qué y el quién; el cuándo todavía se escapa." },
      { n: "Inversión en relaciones", en: "Relationship investment", d: "La atención realmente dedicada a cada relación de trabajo.", ex: "Las respuestas a su equipo esperan más que las que van hacia arriba." },
      { n: "Reconocimiento", en: "Recognition", d: "Reconocer el esfuerzo tanto como el resultado.", ex: "Ha reconocido los resultados más que el esfuerzo en esta ventana." },
      { n: "Ritmo sostenible", en: "Sustainable rhythm", d: "Un ritmo que se sostiene en el tiempo, para usted y para el equipo.", ex: "Su envío nocturno ha aumentado en las últimas dos semanas." },
      { n: "Desarrollo", en: "Development focus", d: "El tiempo dedicado a aprender y a hacer crecer.", ex: "Su tiempo de aprendizaje desapareció en la semana cinco y no ha vuelto." },
      { n: "Conectar la organización", en: "Connecting the organisation", d: "Crear vínculos que viven sin usted. (Senior)", ex: "La pareja que creó continuó tres veces sin usted." },
    ],
    exampleLabel: "Ejemplo de lectura",
    levels: "Cuatro etapas con nombre, nunca una nota: Emerging, Establishing, Consistent, Multiplying. Y Building Evidence cuando aún no hay suficientes ejemplos.",
    note: "En los periodos de cambio se añaden dos comportamientos: explicar el cambio y avanzar pese a la incertidumbre.",
    link: "Ver la metodología completa",
  },
  how: {
    eyebrow: "La puesta en marcha",
    title: "Tres pasos, ninguna formación que organizar",
    steps: [
      { title: "Cada uno da su consentimiento", text: "Cada manager conecta su propia cuenta y puede desconectarla cuando quiera. Sin su permiso, no se lee nada." },
      { title: "La evidencia se acumula", text: "Cayus observa las últimas 8 semanas y las compara con las anteriores. Cada texto se lee una vez y se borra." },
      { title: "Cada uno abre su pestaña", text: "En Teams, cada uno abre su propia vista. Sin nueva contraseña, sin herramienta nueva que aprender." },
    ],
    promise: "Ninguna nota, porcentaje o clasificación sale nunca de Cayus.",
    europe: "Pensado para Europa: RGPD, diálogo con el comité de empresa, conforme al reglamento europeo de IA",
    link: "Ver seguridad y conformidad",
  },
  trust: {
    eyebrow: "Confianza",
    title: "Por qué RRHH confía en nosotros",
    lead: "Lo que ve un manager solo lo ve él. RRHH solo ve grupos, descritos con palabras.",
  },
  liveCard: {
    label: "Esta semana",
    practice: "Recognition",
    from: "Establishing",
    to: "Consistent",
    evidence: "El comportamiento se sostiene desde hace cuatro semanas",
  },
  wallViz: {
    label: "El muro, en una imagen",
    inLabel: "Lo que entra",
    inItems: ["«¿Puedes mirar esto?»", "Un punto de 30 minutos", "«Gracias, buen trabajo»"],
    wall: "EL MURO",
    outLabel: "Lo que sale",
    outItems: ["Clear asks · Consistent", "Recognition · Establishing"],
    note: "Cada mensaje se lee una vez y luego se borra. Solo salen palabras: nunca una puntuación, nunca un nombre, nunca el texto.",
  },
  pilotTimeline: {
    label: "Ocho semanas, tres momentos",
    steps: [
      { w: "Semana 1", t: "Puesta en marcha", d: "Una hora, dentro de su propio Teams." },
      { w: "Semanas 2-3", t: "Las primeras evidencias", d: "Cada uno abre su propia pestaña." },
      { w: "Semana 4", t: "Los primeros comportamientos", d: "Una sola cosa por manager." },
      { w: "Semana 8", t: "Presentación al comité", d: "Lo que se movió, con evidencia." },
    ],
  },
  beforeAfter: {
    eyebrow: "Antes / después",
    title: "El mismo trimestre, visto de dos maneras.",
    lead: "Arrastre el tirador: a la izquierda lo que ve hoy, a la derecha lo que vería con Cayus.",
    beforeLabel: "Sin Cayus",
    afterLabel: "Con Cayus",
    before: [
      "Se entera de que un manager se ha descolgado el día que renuncia",
      "La encuesta recoge lo que cada uno quiere declarar",
      "«Hay que comunicar mejor»: nadie sabe qué hacer el lunes",
      "El presupuesto de desarrollo se defiende con cifras de asistencia",
    ],
    after: [
      "La relación que se enfría se ve cuando un café todavía lo arregla",
      "Ve lo que pasa este mes, unidad por unidad",
      "Cada manager tiene una sola cosa que trabajar esta semana",
      "Muestra al comité un cambio realmente observado",
    ],
    hint: "Arrastre para comparar",
  },
  brief: {
    navLabel: "Dosier ejecutivo",
    eyebrow: "Dosier ejecutivo",
    title: "Dos páginas para poner sobre la mesa de su comité.",
    lead: "Todo lo necesario para presentar Cayus internamente: qué se mide, qué no sale nunca y cómo funciona un piloto. Nada que leer en diagonal.",
    bullets: [
      "El punto ciego: lo que sus KPIs no muestran",
      "Las seis prácticas seguidas y las cuatro vistas (manager, senior, RRHH, comité)",
      "El muro: lo que cruza y lo que nunca cruza",
      "Privacidad, RGPD, Reglamento europeo de IA y el desarrollo del piloto",
    ],
    format: "PDF · 2 páginas · listo para imprimir",
    form: {
      name: "Nombre y apellidos",
      namePlaceholder: "Ana García",
      email: "Email profesional",
      emailPlaceholder: "nombre@empresa.com",
      company: "Empresa",
      role: "Cargo (opcional)",
      rolePlaceholder: "Director de RRHH, director general…",
      submit: "Recibir el dosier",
      submitting: "Enviando…",
      note: "Descarga inmediata y una copia en su buzón. Sin newsletter.",
    },
    success: {
      title: "Listo",
      text: "La descarga se abre en una pestaña nueva. También le hemos enviado una copia del enlace por email.",
      download: "Descargar el dosier",
      toast: "Dosier enviado. La descarga comienza.",
    },
    error: "La descarga no pudo iniciarse. Inténtelo de nuevo.",
  },
  finalCta: {
    badge: "5 plazas de piloto",
    title: "Mida lo que sus KPIs no ven.",
    button: "Ver qué mediría Cayus",
  },
  product: {
    ctaSection: "Treinta minutos bastan para ver qué aspecto tendría en su organización.",
    managers: {
      eyebrow: "Para managers",
      title: "Ser un mejor manager, una semana a la vez.",
      intro: "Ninguna formación que organizar, ninguna herramienta que aprender. Cayus vive en Teams y propone un solo gesto concreto, y luego muestra que ha arraigado.",
      promises: ["Un foco por semana, nunca una lista de reproches", "Dentro de Teams, sin nueva contraseña", "Sus palabras siguen siendo suyas"],
      cta: "Ver qué diría Cayus a sus managers",
      blocks: [
        { img: `${P}/02-manager-focus.webp`, caption: "El foco de la semana", heading: "Un gesto concreto, no un informe de cuarenta páginas", text: "Una sola práctica que trabajar, en una frase. Se valida cuando se sostiene en varios contextos: evidencia, no promesa." },
        { img: `${P}/03-manager-verbatim.webp`, caption: "Sus palabras, visibles solo para usted", heading: "Ve lo que ha contado, con sus propias palabras", text: "Detrás de cada lectura, las frases exactas que la produjeron. Sabe qué formular de otro modo en el próximo mensaje.", box: "Sus propias palabras, visibles solo para él y solo con el panel abierto. Nunca almacenadas, nunca puntuadas, nunca compartidas." },
        { img: `${P}/04-manager-staircase.webp`, caption: "Cuatro niveles con nombre", heading: "Una progresión, no una nota", text: "Se avanza cuando la práctica se sostiene en el tiempo, no por una buena semana. Y nadie se compara con nadie." },
        { img: `${P}/05-manager-habits.webp`, caption: "Las seis prácticas seguidas", heading: "Seis prácticas que lo cambian todo para un equipo", text: "Peticiones claras, relaciones, reconocimiento, ritmo, desarrollo. Cuando falta evidencia, Cayus lo dice en lugar de adivinar." },
      ],
    },
    "senior-managers": {
      eyebrow: "Para senior managers",
      title: "No puede estar en todo. Cayus le dice dónde mirar.",
      intro: "Su práctica y la salud de sus relaciones directas, desde su propia agenda. La relación que se debilita se vuelve visible antes de romperse.",
      promises: ["La misma lectura que sus managers, a su nivel", "Desde su propia agenda, nunca su vista privada", "Una señal antes de la renuncia"],
      cta: "Ver qué revelaría Cayus en su equipo",
      blocks: [
        { img: `${P}/06-senior-two-clocks.webp`, caption: "Su práctica, su trayectoria", heading: "Lo que hace esta semana y lo que está construyendo", text: "La misma lectura que sus managers, a su nivel. Por fin hablan el mismo idioma." },
        { img: `${P}/08-senior-span.webp`, caption: "Sus relaciones directas, una a una", heading: "Sus seis, una relación a la vez", text: "Ritmo de los 1:1, peticiones enviadas, desarrollo dado. Tres estados de un vistazo para saber quién le necesita esta semana." },
        { img: `${P}/09-senior-balance.webp`, caption: "Lo que dio y lo que se escapó", heading: "Su atención es un recurso. Vea a dónde fue.", text: "Una relación nunca se enfría a propósito. Cayus lo muestra cuando recuperarla todavía cuesta un café, no una contratación." },
        { img: `${P}/07-senior-staircase.webp`, caption: "La progresión a nivel senior", heading: "Una escala más: conectar la organización", text: "Crear vínculos que siguen viviendo sin usted.", box: "Ve lo que hizo. Nunca lo que Cayus dijo a sus managers, nunca su nivel." },
      ],
    },
    rh: {
      eyebrow: "Para RRHH",
      title: "Por fin una lectura del liderazgo entre dos encuestas anuales.",
      intro: "Cada práctica, cada unidad, mes a mes, descrita en palabras, nunca en personas. Y sus programas de desarrollo se vuelven demostrables ante el comité.",
      promises: ["Continuo, no una foto una vez al año", "Grupos de 8 personas mínimo, nunca nombres", "Su presupuesto de desarrollo, probado por un movimiento observado"],
      cta: "Ver qué leería Cayus en su organización",
      blocks: [
        { img: `${P}/10-hr-three-clocks.webp`, caption: "La lectura de conjunto", heading: "Ya no espera la encuesta del año que viene", text: "Donde la encuesta anual cuenta lo que pasó, Cayus muestra lo que está pasando." },
        { img: `${P}/12-hr-units.webp`, caption: "Prácticas × unidades", heading: "Dónde sube, dónde se escapa", text: "El nivel dominante de cada unidad y su movimiento contra sus propios meses. Sabe dónde colocar un acompañamiento, y dónde no hay nada que arreglar." },
        { img: `${P}/11-hr-matrix.webp`, caption: "Los playbooks", heading: "Su presupuesto de desarrollo, por fin defendible", text: "Cada programa lleva un movimiento esperado y una fecha. Ante el comité ya no presenta asistencia: presenta un cambio observado.", box: "Lo que RRHH ve: equipos, tendencias, evoluciones. Lo que nunca ve: una persona, su etapa, el contenido de un mensaje." },
      ],
    },
    "comite-executif": {
      eyebrow: "Para el comité ejecutivo",
      title: "Lo que la organización sostiene de verdad, y en qué acaban sus decisiones.",
      intro: "La estrategia no muere en la sala, se disuelve entre dos comités. Cayus muestra lo que de verdad se sostiene y el destino de cada decisión, incluido el ritmo del comité.",
      promises: ["Tendencias de grupo, nunca personas", "Cada decisión con responsable y fecha", "El ritmo del comité incluido"],
      cta: "Ver qué mediría Cayus",
      blocks: [
        { img: `${P}/14-exco-two-clocks.webp`, caption: "Lo que se sostiene en el tiempo", heading: "Lo que se sostiene, no lo que se anunció", text: "Ve si la cultura que describe es la que se practica." },
        { img: `${P}/15-exco-questions.webp`, caption: "El destino de las decisiones", heading: "En qué acabó la última decisión", text: "Held, Partly, Not yet, con responsable y fecha. Nada se disuelve en silencio entre dos sesiones." },
        { img: `${P}/16-exco-decisions.webp`, caption: "La sombra del comité", heading: "Su ritmo es el que la organización copia", text: "El ritmo del comité, leído como tendencia de grupo y nunca atribuido a un miembro, con los gestos concretos para ajustarlo.", quote: "El ritmo que usted mantiene es el que la organización copia." },
        { img: `${P}/17-exco-shadow.webp`, caption: "El registro de decisiones", heading: "El acta se escribe sola", text: "Cada decisión, su responsable y su fecha de revisión, copiables en un clic en el acta." },
      ],
    },
  },
};

const REFONTE = { fr, en, es };

export const useRefonte = (language) => REFONTE[language] || REFONTE.fr;
export default REFONTE;
